import { TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./card";
import { cn } from "@utils/design-utils";
import { useMemo, type ReactNode, type CSSProperties } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  type LabelProps,
} from "recharts";

interface AreaChartData {
  name: string;
  value: number;
}

interface AreaChartProps {
  data: AreaChartData[];
  title?: string;
  height?: number | string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  color?: string;
  tooltipLabel?: string;
  tooltipPrefix?: string;
  hideHeader?: boolean;
  headerClassName?: string;
  cardClassName?: string;
  contentClassName?: string;
  headerIcon?: ReactNode;
  cardStyle?: CSSProperties;
  chartContainerClassName?: string;
  yAxisLabelProps?: Partial<LabelProps>;
  children?: ReactNode;
  barSize?: number; // kept for API parity with BarChartComponent callers
}

export function AreaChartComponent({
  data,
  title = "Trends Over Time",
  height = 300,
  xAxisLabel = "Category",
  yAxisLabel = "Value",
  color = "#1B325F",
  tooltipLabel = "Value",
  tooltipPrefix = "",
  hideHeader = false,
  headerClassName,
  cardClassName,
  contentClassName,
  headerIcon,
  cardStyle,
  chartContainerClassName,
  yAxisLabelProps,
  children,
}: AreaChartProps) {
  const gradientId = useMemo(
    () => `areaGradient-${Math.random().toString(36).slice(2)}`,
    [],
  );

  return (
    <Card className={cn(cardClassName)} style={cardStyle}>
      {!hideHeader && (
        <CardHeader className={headerClassName}>
          <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            {headerIcon ?? <TrendingUp className="h-5 w-5" />}
            {title}
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className={cn("flex flex-col", contentClassName)}>
        {children}
        <div className={cn("w-full", children ? "flex-1" : "", chartContainerClassName)}>
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={data}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                label={{ value: xAxisLabel, position: "insideBottom", offset: -5 }}
              />
              <YAxis
                label={{
                  value: yAxisLabel,
                  angle: -90,
                  position: "insideLeft",
                  style: { fontSize: 12, fontWeight: 500 },
                  ...yAxisLabelProps,
                }}
              />
              <Tooltip
                formatter={(value: number) => [`${tooltipPrefix}${value.toLocaleString()}`, tooltipLabel]}
                labelFormatter={(label) => `${label}`}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                fill={`url(#${gradientId})`}
                dot={{ stroke: color, strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
